import { fetchAuthSession, signOut } from "aws-amplify/auth"

import { ensureAmplifyConfigured } from "@/lib/auth/amplify-config"

export type Tokens = {
	readonly accessToken: string
}

let initPromise: Promise<void> | null = null
let cachedTokens: Tokens | null = null

export function clearAuthCache(): void {
	cachedTokens = null
	initPromise = null
}

export async function initAuth(): Promise<void> {
	if (!initPromise) {
		initPromise = refreshCachedTokens({ forceRefresh: false }).then(() => undefined)
	}
	return initPromise
}

export function getAuthenticatedTokens(): Tokens | null {
	return cachedTokens
}

export async function getAccessTokenJwt(args?: { forceRefresh?: boolean }): Promise<string | null> {
	const forceRefresh = typeof args === "object" && args !== null && args.forceRefresh === true
	const tokens = await refreshCachedTokens({ forceRefresh })
	if (!tokens) return null
	return tokens.accessToken
}

export async function logout(): Promise<void> {
	ensureAmplifyConfigured()

	try {
		await signOut()
	} catch (err) {
		console.error(" signOut() failed:", err)
		// Continue anyway - we still want to clear local state
	}
	cachedTokens = null
}

export async function refreshAuthTokens(): Promise<Tokens | null> {
	// Force-refresh cached tokens after auth state changes.
	return await refreshCachedTokens({ forceRefresh: true })
}

function looksLikeJwt(value: string): boolean {
	// JWTs are typically three base64url segments separated by dots.
	return value.split(".").length === 3
}

function extractJwtString(token: unknown): string | null {
	if (typeof token === "string") {
		return looksLikeJwt(token) ? token : null
	}
	if (!token || typeof token !== "object") return null

	if ("jwtToken" in token) {
		const jwtToken = (token as Record<string, unknown>)["jwtToken"]
		if (typeof jwtToken === "string" && looksLikeJwt(jwtToken)) return jwtToken
	}

	const maybeToString = (token as { toString?: unknown }).toString
	if (typeof maybeToString === "function") {
		const str = String(token)
		if (looksLikeJwt(str)) return str
	}

	return null
}

async function refreshCachedTokens(args: { forceRefresh: boolean }): Promise<Tokens | null> {
	ensureAmplifyConfigured()

	let session: Awaited<ReturnType<typeof fetchAuthSession>>
	try {
		session = await fetchAuthSession({ forceRefresh: args.forceRefresh })
	} catch {
		cachedTokens = null
		return null
	}

	const tokens = session.tokens
	if (!tokens) {
		cachedTokens = null
		return null
	}

	const accessToken = tokens.accessToken
	if (!accessToken) {
		cachedTokens = null
		return null
	}

	const jwt = extractJwtString(accessToken)
	if (!jwt) {
		cachedTokens = null
		return null
	}

	cachedTokens = { accessToken: jwt }
	return cachedTokens
}
